import { DemandInfo } from '~/api/demands/_types';
import { FilterCardColumnConfig, FilterDataType } from '~/components/base/filters/_types';
import { AppTableColumnConfig } from '~/components/base/tables/_types';
import { t } from '~/plugins/i18n';
import { formatNumber } from '~/utils/localeUtils';

export const getDemandsOverviewColumnConfig = (filter?: string[]): AppTableColumnConfig[] => {
    const columns: AppTableColumnConfig[] = [
        {
            name: 'Id',
            label: '',
            translatedLabel: { key: 'demands.overview.columns.id' },
            field: 'Id',
            align: 'left',
            classes: 'text-bold',
            sortable: true,
        },
        {
            name: 'ReferenceNumber',
            label: '',
            translatedLabel: { key: 'demands.overview.columns.referenceNumber' },
            field: 'ReferenceNumber',
            align: 'left',
            sortable: true,
        },
        {
            name: 'PartnerCardName',
            label: '',
            translatedLabel: { key: 'demands.overview.columns.partner' },
            field: (val: DemandInfo) => val.Partner.CardName,
            align: 'left',
            sortable: true,
        },
        {
            name: 'UserCustomerName',
            label: '',
            translatedLabel: { key: 'demands.overview.columns.customer' },
            field: (val: DemandInfo) => val.Customer.FullName,
            align: 'left',
            sortable: true,
        },
        {
            name: 'CreatedAt',
            label: '',
            translatedLabel: { key: 'demands.overview.columns.createdAt.eq' },
            field: 'CreatedAt',
            align: 'left',
            sortable: true,
        },
        {
            name: 'RequiredAt',
            label: '',
            translatedLabel: { key: 'demands.overview.columns.requiredAt.eq' },
            field: 'ShipAt',
            align: 'left',
            sortable: true,
        },
        {
            name: 'PriceTotal',
            label: '',
            translatedLabel: { key: 'demands.overview.columns.price' },
            field: (val) =>
                val.Price ? formatNumber(val.Price.Value) + ' ' + t(`generic.currency.${val.Price?.Currency}`) : null,
            align: 'left',
            sortable: true,
        },
        {
            name: 'Status',
            label: '',
            translatedLabel: { key: 'demands.overview.columns.status' },
            field: 'Status',
            align: 'left',
            sortable: true,
        },
        {
            name: 'Actions',
            label: '',
            field: '',
            align: 'right',
        },
    ];

    return filter == null ? columns : columns.filter((x) => !filter.find((y) => x.name === y));
};

export const getDemandsOverviewFilterColumnConfig = (): FilterCardColumnConfig[] => [
    {
        dataType: FilterDataType.String,
        key: 'ReferenceNumber',
        label: 'demands.overview.columns.referenceNumber',
        additionalFilter: false,
    },
    {
        dataType: FilterDataType.Enum,
        key: 'PartnerId',
        label: 'demands.overview.columns.partner',
        additionalFilter: false,
        multiple: true,
        autocomplete: true,
        optionsKey: 'Id',
        optionsLabelKey: 'CardName',
    },
    {
        dataType: FilterDataType.Enum,
        key: 'UserCustomerId',
        label: 'demands.overview.columns.customer',
        additionalFilter: false,
        multiple: true,
        autocomplete: true,
        optionsKey: 'Id',
        optionsLabelKey: 'FullName',
    },
    {
        dataType: FilterDataType.Date,
        mode: 'range',
        key: 'CreatedAt',
        labelFrom: 'demands.overview.columns.createdAt.from',
        labelTo: 'demands.overview.columns.createdAt.to',
        additionalFilter: false,
    },
    {
        dataType: FilterDataType.Date,
        mode: 'range',
        key: 'RequiredAt',
        labelFrom: 'demands.overview.columns.requiredAt.from',
        labelTo: 'demands.overview.columns.requiredAt.to',
        additionalFilter: false,
    },
    {
        dataType: FilterDataType.Enum,
        key: 'Status',
        label: 'demands.overview.columns.status',
        additionalFilter: false,
        multiple: true,
    },
];

export const getDemandLinesDetailColumnConfig = (filter?: string[]): AppTableColumnConfig[] => {
    const columns: AppTableColumnConfig[] = [
        {
            name: 'Item',
            label: '',
            translatedLabel: { key: 'demands.detail.lines.columns.item' },
            field: 'Item',
            align: 'left',
        },
        {
            name: 'Nomenclature',
            label: '',
            translatedLabel: { key: 'demands.detail.lines.columns.nomenclature' },
            field: 'Nomenclature',
            align: 'left',
        },
        {
            name: 'RequiredAt',
            label: '',
            translatedLabel: { key: 'demands.detail.lines.columns.requiredAt' },
            field: (val) => val.RequiredAt?.toLocaleString(),
            align: 'center',
        },
        {
            name: 'Quantity',
            label: '',
            translatedLabel: { key: 'demands.detail.lines.columns.quantity' },
            field: (val) => `${val.Quantity}x`,
            align: 'center',
        },
        {
            name: 'Price',
            label: '',
            translatedLabel: { key: 'demands.detail.lines.columns.price' },
            field: 'Price',
            align: 'right',
        },
    ];

    return filter == null ? columns : columns.filter((x) => !filter.find((y) => x.name === y));
};

export const getDemandFormItemsColumnConfig = (filter?: string[]): AppTableColumnConfig[] => {
    const columns: AppTableColumnConfig[] = [
        {
            name: 'Actions',
            label: '',
            field: '',
            align: 'left',
            headerClasses: 'bg-background',
        },
        {
            name: 'Code',
            label: '',
            translatedLabel: { key: 'demands.form.items.columns.Code' },
            field: 'Code',
            align: 'left',
            classes: 'text-bold',
        },
        {
            name: 'Name',
            label: '',
            translatedLabel: { key: 'demands.form.items.columns.Name' },
            field: 'Name',
            align: 'left',
        },
        {
            name: 'Nomenclature',
            label: '',
            translatedLabel: { key: 'demands.form.items.columns.Nomenclature' },
            field: 'Nomenclature',
            align: 'right',
        },
        {
            name: 'Quantity',
            label: '',
            translatedLabel: { key: 'demands.form.items.columns.Quantity' },
            field: 'Quantity',
            align: 'left',
        },
        {
            name: 'RequiredAt',
            label: '',
            translatedLabel: { key: 'demands.form.items.columns.RequiredAt' },
            field: 'RequiredAt',
            align: 'left',
        },
        {
            name: 'Price',
            label: '',
            translatedLabel: { key: 'demands.form.items.columns.Price' },
            field: 'Price',
            align: 'right',
        },
    ];

    return filter == null ? columns : columns.filter((x) => !filter.find((y) => x.name === y));
};
